import { useState } from 'react';

import type { BookingGarment, ChangeGarmentStateInput, GarmentState } from '@/api/bookings';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { TailorPicker } from './TailorPicker';
import { Button, Notice } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { StatusBadge } from '@/components/StatusBadge';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { humanizeEnum } from '@core/format/text';
import { alterationNarrative, knownMeasures } from '@/pages/print/alterationNarrative';
import {
  addAlteration,
  alterationProblems,
  freeRows,
  fromDetails,
  measureGroups,
  measureLabel,
  measureValue,
  olderChanges,
  removeAlteration,
  setMeasure,
  tailorProblems,
  todayForDateInput,
  toPreview,
  updateAlteration,
} from '@core/bookings/alterations';
import { movesFor } from '@core/bookings/garmentMoves';
import { alterationFields, handOverWords } from '@core/bookings/sendToTailor';
import { garmentTone } from '@core/bookings/tones';
import { withReference } from '@core/units/referenceNumber';

/**
 * Moving one garment along the fulfilment track.
 *
 * <p>
 * The alteration fields appear only when the destination is `Alteration`, because that is the one
 * move where they mean anything — and they are what makes the move useful rather than decorative. A
 * garment at the tailor with no job number, no tailor and no due date is a garment nobody can chase,
 * and the alteration docket the print module already renders has nothing to print.
 * </p>
 *
 * <p>
 * <strong>Reversible versus repair is asked out loud, not defaulted.</strong> §11 draws that line
 * because getting it wrong destroys stock: a customer fitting alteration is temporary and usually
 * reversed, a unit repair is permanent, and a tailor who trims the excess on the first has cost the
 * shop the garment. The screen makes it a deliberate answer.
 * </p>
 *
 * <p>
 * <strong>The charge is recorded whether or not it is billed.</strong> Free to the customer is never
 * free to the shop — the tailor is paid either way, and that money is part of the garment's lifetime
 * cost, which §20's ROI is computed from. Modelling "free" as a zero charge throws the cost side
 * away permanently, so billable is a separate switch beside the amount rather than a way of typing 0.
 * </p>
 */
export function GarmentStateDrawer({
  garment,
  branchId,
  busy = false,
  onClose,
  onMove,
}: {
  garment: BookingGarment;
  /** The booking branch, so the tailor list is the one this shop actually uses. */
  branchId: number;
  busy?: boolean;
  onClose: () => void;
  onMove: (input: ChangeGarmentStateInput) => void;
}) {
  const moves = movesFor(garment.state);

  /**
   * What the chip row offers.
   *
   * A garment already with the tailor gets `Alteration` back as an option, which
   * `garmentMoves.ts` correctly does not list — staying put is not a transition. It is here because
   * the tailor rings to say the sleeve wants another half inch, and the alternative is a second
   * endpoint writing the same table. The server permits exactly this same-state call, and only when
   * it carries measurements.
   */
  const offered: readonly GarmentState[] =
    garment.state === 'Alteration' ? ['Alteration', ...moves] : moves;

  // Defaults to the first real move — for a garment at the tailor that is "Ready", which is what a
  // clerk opening this drawer usually means. Correcting the docket is the deliberate second choice.
  const [state, setState] = useState<GarmentState | null>(moves[0] ?? offered[0] ?? null);
  const [reason, setReason] = useState('');
  // Read-only now, and no longer state: the server issues the number and stamps it on the line, so
  // there is nothing here for a clerk to change and nothing to hold between renders.
  const jobNumber = garment.alterationJobNumber ?? '';
  const [tailor, setTailor] = useState(garment.alterationTailorName ?? '');
  const [tailorVendorId, setTailorVendorId] = useState(garment.alterationTailorVendorId);
  const [tailorEmployeeId, setTailorEmployeeId] = useState(garment.alterationTailorEmployeeId);
  const handOver = handOverWords({
    tailorName: tailor,
    vendorId: tailorVendorId,
    employeeId: tailorEmployeeId,
  });
  const [dueOn, setDueOn] = useState(garment.alterationDueOn ?? '');
  const [charge, setCharge] = useState(
    garment.alterationCharge === null ? '' : String(garment.alterationCharge),
  );
  const [billable, setBillable] = useState(garment.alterationBillable);
  const [reversible, setReversible] = useState(garment.alterationIsReversible);

  // Seeded from what the garment already carries, with a blank row at the bottom so there is always
  // somewhere to type. Blanks are dropped on the way out — an untouched row is not a mistake.
  const [work, setWork] = useState(() => addAlteration(fromDetails(garment.alterations)));

  useEscapeKey(onClose);

  // Only the alteration move has fields; every other one is a chip and a note. Nothing to focus is
  // an ordinary answer here, and the hook returns quietly rather than reaching further.
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const toAlteration = state === 'Alteration';

  // The picker refuses a past date outright, so the ordinary slip never reaches the notice below.
  const earliestDueDate = todayForDateInput();

  // Both recomputed per keystroke, which is the whole reason this is a pure module beside the
  // screen rather than a round trip. The due date is in the same list as the measurements because
  // it is the same refusal to the clerk — one notice naming everything that is not ready yet.
  const problems = toAlteration ? [...tailorProblems(dueOn), ...alterationProblems(work)] : [];
  const preview = toAlteration ? alterationNarrative(toPreview(work)) : '';

  return (
    <div data-shortcut-layer className="fixed inset-0 z-9999 flex print:hidden">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 cursor-default bg-[var(--overlay-scrim)]"
      />

      {/*
        Enter moves to the next field, the way it does in every drawer — see `enterKeyNavigation`.
        This panel is hand-rolled rather than a `FormDrawer`, so the handler goes on the `<aside>`
        and covers the alteration fields inside it by bubbling.

        560px is the width every other drawer in the product uses. This one was 460, which was
        survivable while the alteration branch was three unlabelled boxes on one line and is not
        once each measurement is a labelled card.
      */}
      <aside
        {...enterKeyScope}
        className="flex w-full max-w-[560px] flex-col border-s border-[var(--overlay-border)] bg-[var(--overlay-surface)]"
      >
        <header className="border-border-color flex items-center justify-between border-b px-5 py-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{garment.itemName}</p>
            <p className="text-default font-mono-nums truncate text-[12px]">
              {withReference(garment.serialCode, garment.referenceNumber)}
            </p>
          </div>
          <Button icon="icon icon-x" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </header>

        <div ref={fields} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          <p className="flex items-center gap-2 text-[13px]">
            <span className="text-default">Now</span>
            <StatusBadge tone={garmentTone(garment.state)}>
              {humanizeEnum(garment.state)}
            </StatusBadge>
          </p>

          {moves.length === 0 ? (
            <Notice tone="info" title="Nothing to do from here">
              {garment.state === 'Ready' || garment.state === 'Allocated'
                ? 'Handing this over happens on the issue counter, with the rest of the wearer’s outfit.'
                : `${humanizeEnum(garment.state)} is where this garment’s track ends.`}
            </Notice>
          ) : (
            <>
              <div>
                <span className="mb-1.5 block text-[12px] font-medium text-gray-900">Move to</span>
                <div className="flex flex-wrap gap-1.5">
                  {offered.map((option) => (
                    <button
                      key={option}
                      type="button"
                      disabled={busy}
                      onClick={() => setState(option)}
                      className={`h-8 cursor-pointer rounded border px-3 text-[12.5px] ${
                        state === option
                          ? 'border-primary bg-primary text-white'
                          : 'border-border-color bg-white text-gray-900 hover:bg-light'
                      }`}
                    >
                      {option === garment.state ? 'Update the docket' : humanizeEnum(option)}
                    </button>
                  ))}
                </div>
              </div>

              {toAlteration && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {/*
                      Stated, not asked.

                      This was a free-text box a clerk typed a number into, and the job it named
                      carried no number of its own — so the number on the card in the workshop and
                      the number on the booking were two unrelated strings that agreed only when
                      somebody was careful. The job issues its own now (`AlterationJob` series) and
                      the booking line takes it, so there is one number and nothing to type.

                      A garment on its way to the tailor for the first time has none yet, which is
                      honest: it does not exist until the move is saved.
                    */}
                    <Field label="Job number" hint="Issued when this is saved">
                      <p className="font-mono-nums flex h-[38px] items-center text-[13px] text-gray-900">
                        {jobNumber || <span className="text-default">Not issued yet</span>}
                      </p>
                    </Field>
                    <TailorPicker
                      name={tailor}
                      vendorId={tailorVendorId}
                      employeeId={tailorEmployeeId}
                      branchId={branchId}
                      disabled={busy}
                      onChange={(next) => {
                        setTailor(next.name);
                        setTailorVendorId(next.vendorId);
                        setTailorEmployeeId(next.employeeId);
                      }}
                    />
                    {/* Required, and not merely as a courtesy to whoever chases the tailor: the
                        garment comes off the rail from today until this date, and a block with no
                        end either does not exist or never lifts. */}
                    <Field label="Due back" required hint="Off the rail until then">
                      {/* The app's picker rather than `type="date"`, which renders in the browser's
                          locale and so ignored the company's chosen format entirely. `disabledDate`
                          is what antd offers in place of the native `min` — same refusal, and it
                          greys the days out in the calendar rather than only rejecting them on
                          submit. */}
                      <DatePicker
                        className="w-full"
                        value={toDayjs(dueOn) ?? null}
                        disabled={busy}
                        disabledDate={(date) => date.format('YYYY-MM-DD') < earliestDueDate}
                        onChange={(value) => setDueOn(toIsoDate(value) ?? '')}
                      />
                    </Field>
                    <Field label="Cost of the work">
                      <input
                        type="number"
                        min={0}
                        value={charge}
                        disabled={busy}
                        onWheel={(event) => event.currentTarget.blur()}
                        onChange={(event) => setCharge(event.target.value)}
                        className={controlClass}
                      />
                    </Field>
                  </div>

                  {/* Sending to an outside tailor is the dispatch — the challan goes as this is
                      saved, so the clerk is told before saving rather than finding it in the queue. */}
                  {handOver && <Notice tone="info" title={handOver} />}

                  {/* The work itself, which had no way to be recorded at all until it was built —
                      and so the tailor's docket, which keys on these rows, never printed in a
                      running shop however completely the fields above were filled in.

                      <strong>It has been three shapes.</strong> A row of three `controlClass`
                      fields, which collapsed: `controlClass` opens with `w-full`, so the number's
                      `w-24` lost the cascade, took the line and squeezed the other two to stumps.
                      Then a labelled card per measurement, which read correctly and was slow — one
                      click to add each row and three fields to fill it. Now the shop's own karigar
                      card: fixed boxes, one per measure, tabbed through.

                      The middle shape's lesson survives both moves: **a tailor's instruction is the
                      one thing on this screen a clerk must be able to re-read before sending it**,
                      so every box is labelled rather than carrying a placeholder that vanishes the
                      moment anything is typed. */}
                  <div>
                    <span className="mb-1.5 block text-[12px] font-medium text-gray-900">
                      What the tailor must do
                    </span>

                    {/*
                      The karigar card, as a grid.

                      <strong>Shaped after the printed card a shop already fills in by hand</strong>
                      — a row of boxes for the upper garment and another for the lower — because the
                      editor this replaced asked for a three-field card *per measurement* plus a
                      click to add each one. Seven measures cost about seven clicks and twenty-one
                      fields; here it is seven boxes and a tab between them.

                      A box holds no row until somebody types in it, and loses the row when it is
                      cleared — so what is on the card is exactly what is being asked for. The rule
                      is `setMeasure`, and it is tested there rather than here.
                    */}
                    {measureGroups.map((group) => (
                      <div key={group.title} className="mb-3">
                        <div className="mb-1.5 flex items-baseline gap-2">
                          <span className="text-[11.5px] font-semibold text-gray-900 uppercase">
                            {group.title}
                          </span>
                          <span className="text-default text-[11px]">{group.hint}</span>
                        </div>

                        <div className="border-border-color bg-border-color grid grid-cols-2 gap-px overflow-hidden rounded-md border sm:grid-cols-3">
                          {group.measures.map((measure) => (
                            <label key={measure} className="flex flex-col gap-1 bg-white p-2">
                              <span className="text-default text-[11px]">
                                {measureLabel(measure)}
                              </span>

                              {/*
                                Eighths, because that is what the trade works in and what
                                `formatInches` renders as vulgar fractions on the docket. Which way
                                a sign goes is said once under the grid rather than on every box —
                                at this size a hint per field would be more legend than form.
                              */}
                              <input
                                type="number"
                                step="0.125"
                                inputMode="decimal"
                                value={measureValue(work, measure)}
                                disabled={busy}
                                placeholder="—"
                                /* A wheel over a focused number input steps its value, so scrolling
                                   this drawer past the field silently rewrites what the tailor is
                                   told. Blurring hands the scroll back to the panel. */
                                onWheel={(event) => event.currentTarget.blur()}
                                onChange={(event) =>
                                  setWork((was) => setMeasure(was, measure, event.target.value))
                                }
                                className="font-mono-nums w-full border-0 bg-transparent p-0 text-[13px] text-gray-900 focus:outline-none"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}

                    <p className="text-default mb-2 text-[11px]">
                      Inches — <b className="text-gray-900">the finished size</b>, chest 46. Leave a
                      box empty for anything the tailor is not touching.
                    </p>
                    {olderChanges(work).length > 0 ? (
                      <div className="mb-2">
                        <Notice tone="warning" title="Changes from an older card">
                          {`${olderChanges(work).join(', ')} still ${olderChanges(work).length === 1 ? 'holds' : 'hold'} a change (minus shortens, plus lets out), not a size. Type the finished size to replace it.`}
                        </Notice>
                      </div>
                    ) : null}

                    {/*
                      Anything the card does not carry — a safa wrap, a dupatta fall, mojris.

                      The vocabulary is open on purpose and the grid must not close it: the docket
                      prints an unrecognised measure with its own name in any language rather than
                      dropping the row, which is what makes the open end safe.
                    */}
                    <datalist id="alteration-measures">
                      {knownMeasures.map((measure) => (
                        <option key={measure} value={measure} />
                      ))}
                    </datalist>

                    <ul className="flex list-none flex-col gap-2 ps-0">
                      {freeRows(work).map((row) => (
                        <li
                          key={row.key}
                          className="border-border-color mb-0 grid list-none grid-cols-[1fr_5.5rem_auto] items-end gap-2 rounded-md border bg-white p-2"
                        >
                          <Field label="Anything else" className="mb-0">
                            <input
                              list="alteration-measures"
                              value={row.measure}
                              disabled={busy}
                              placeholder="Safa wrap"
                              onChange={(event) =>
                                setWork((was) =>
                                  updateAlteration(was, row.key, { measure: event.target.value }),
                                )
                              }
                              className={controlClass}
                            />
                          </Field>

                          <Field label="Inches" className="mb-0">
                            <input
                              type="number"
                              step="0.125"
                              value={row.figure}
                              disabled={busy}
                              onWheel={(event) => event.currentTarget.blur()}
                              placeholder="46"
                              onChange={(event) =>
                                setWork((was) =>
                                  updateAlteration(was, row.key, { figure: event.target.value }),
                                )
                              }
                              className={controlClass}
                            />
                          </Field>

                          {/* Named rather than an unlabelled glyph: at this size an × beside two
                              fields reads as "clear this box" as easily as "remove this row", and
                              the two are very different mistakes. */}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setWork((was) => removeAlteration(was, row.key))}
                            className="text-default hover:text-danger mb-1.5 flex cursor-pointer items-center gap-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <i className="icon icon-x text-[11px]" />
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setWork((was) => addAlteration(was))}
                      className="text-primary mt-2 cursor-pointer text-[12px] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      + Something the card does not carry
                    </button>
                  </div>

                  {/* The sentence the tailor will actually read, under the clerk's hands as they
                      type. Structured in, narrative out — this is the half of that asymmetry the
                      counter never got to see. */}
                  {preview !== '' && (
                    <div className="border-border-color rounded border bg-light p-3">
                      <p className="text-default mb-1 text-[11px] uppercase">The docket will say</p>
                      <p className="text-[12.5px] whitespace-pre-line text-gray-900">{preview}</p>
                    </div>
                  )}

                  {problems.length > 0 && (
                    <Notice tone="danger" title="Not ready to send">
                      <ul className="list-disc ps-4">
                        {problems.map((problem) => (
                          <li key={problem}>{problem}</li>
                        ))}
                      </ul>
                    </Notice>
                  )}

                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={billable}
                      disabled={busy}
                      onChange={(event) => setBillable(event.target.checked)}
                      className="mt-0.5"
                    />
                    <span className="text-[12.5px]">
                      <b className="text-gray-900">Bill this to the customer</b>
                      <span className="text-default block">
                        Untick and the shop absorbs it. The cost is still recorded either way — the
                        tailor is paid regardless, and that money is part of the garment’s lifetime
                        return.
                      </span>
                    </span>
                  </label>

                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={reversible}
                      disabled={busy}
                      onChange={(event) => setReversible(event.target.checked)}
                      className="mt-0.5"
                    />
                    <span className="text-[12.5px]">
                      <b className="text-gray-900">Reversible fitting, not a permanent repair</b>
                      <span className="text-default block">
                        A fitting alteration is undone when the garment comes back. Leave this
                        ticked unless the work is meant to be permanent — a tailor who trims the
                        excess on a reversible alteration has destroyed the garment.
                      </span>
                    </span>
                  </label>

                  {!reversible && (
                    <Notice tone="warning" title="This will be permanent">
                      The docket will say so in bold, with the garment’s replacement value printed
                      beside it.
                    </Notice>
                  )}
                </>
              )}

              <Field label="Note">
                <input
                  value={reason}
                  disabled={busy}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why, if it is not obvious"
                  className={controlClass}
                />
              </Field>
            </>
          )}
        </div>

        {moves.length > 0 && (
          <footer className="border-border-color flex justify-end gap-2 border-t bg-white px-5 py-3">
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              data-enter-key-commit
              shortcut="save"
              disabled={busy || state === null || problems.length > 0}
              onClick={() => {
                if (state === null) {
                  return;
                }

                onMove({
                  state,
                  reason: reason.trim() || null,
                  // The core's builder, which the phone's tailor screen sends through too: never
                  // a job number (the server issues it), and the card always, even empty.
                  ...(toAlteration
                    ? alterationFields({
                        tailorName: tailor,
                        vendorId: tailorVendorId,
                        employeeId: tailorEmployeeId,
                        dueOn,
                        charge,
                        billable,
                        reversible,
                        work,
                        reason,
                      })
                    : {}),
                });
              }}
            >
              {state === null ? 'Move' : `Mark ${humanizeEnum(state).toLowerCase()}`}
            </Button>
          </footer>
        )}
      </aside>
    </div>
  );
}

// See `components/Field.tsx` — this drawer's private copy was one of four that had drifted apart.
